import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useEffect,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Address, CatalogItem, User, UserProfile } from 'shared'

import { useAuth } from './AuthProvider'
import { useAnalytics } from './AnalyticsProvider'
import useAddress from 'utils/hooks/useAddress'

import {
  generalValidationSchema,
  socialValidationSchema,
  aboutValidationSchema,
  contentValidationSchema,
  agreementValidationSchema,
  reasonsToJoinValidationSchema,
  ProfileFormValues,
  beautyValidationSchema,
  sizesValidationSchema,
} from 'components/ProfileForm/config'

import ProfileFormSocial from 'components/ProfileForm/ProfileFormSocial'
import ProfileFormAbout from 'components/ProfileForm/ProfileFormAbout'
import ProfileFormGeneral from 'components/ProfileForm/ProfileFormGeneral'
import ProfileFormAgreement from '../../components/ProfileForm/ProfileFormAgreement'
import ReviewExperience from '../../components/ProfileForm/ProfileFormReviewExperience'
import ProfileFormApparelSizes from 'components/ProfileForm/ProfileFormApparelSizes'
import ProfileFormGetStarted from 'components/ProfileForm/ProfileFormGetStarted'
import ProfileFormReasonsToJoin from 'components/ProfileForm/ProfileFormReasonsToJoin'
import ProfileFormBeauty from 'components/ProfileForm/ProfileFormBeauty'

import { updateProfile } from 'api/user'
import useAttributes from 'utils/hooks/useAttributes'
import { checkIsEnglish } from 'components/ProfileForm/helpers'
import { ANALYTICS_EVENT_NAMES } from 'constants/analytics'

interface ProfileFormStep {
  id: number
  Component: React.FC
  validationSchema?: object
  onForward: () => void
  onBack: () => void
}

interface ProfileFormContextProps {
  activeStep: number
  onStepChange: (id: number, currValues: object | null) => void
  onCountryChange: (newCode: string) => void
  steps: ProfileFormStep[]
  totalSteps: number
  user: User | null
  stepCount: number
  isUserUpdating: boolean
}

const ProfileFormContext = createContext<ProfileFormContextProps>({
  activeStep: 0,
  onStepChange: () => null,
  onCountryChange: () => null,
  steps: [],
  totalSteps: 0,
  user: null,
  stepCount: 1,
  isUserUpdating: false,
})

const BEAUTY_QUESTIONS = [
  'Cosmetics: Makeup products for the face, like foundation, eyeshadow, or lipstick',
  'Skincare and bodycare: Topical products for the skin like serums, creams, and body lotions',
  'Haircare: Cleansing and styling products like shampoos, masks, and dye',
  'Perfumes or fragrances',
]

const BEAUTY_QUESTION_INDEX = 6

const ProfileFormProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isUserFetching } = useAuth()
  const [searchParams, setParams] = useSearchParams()
  const { attributes: allAttributes } = useAttributes(['languages'])
  const { analyticsTrack } = useAnalytics()

  const { languages } = allAttributes

  const queryClient = useQueryClient()

  const [activeStep, setActiveStep] = useState(
    searchParams.get('profile-form-step')
      ? Number(searchParams.get('profile-form-step'))
      : 0
  )

  const [country, setCountry] = useState('US')
  const { addressField, isUKBasedUser } = useAddress({
    country,
  } as Address)

  useEffect(() => {
    if (user?.profile?.address?.country) {
      setCountry(user?.profile?.address?.country)
    }
  }, [user?.profile?.address?.country])

  const updateUser = useMutation({
    mutationKey: ['profile_form'],
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  useEffect(() => {
    analyticsTrack(`${ANALYTICS_EVENT_NAMES.application}: Launched`)
  }, [])

  const onCountryChange = (newCode: string) => {
    setCountry(newCode)
  }

  const onStepChange = (id: number, currValues: object | null) => {
    if (currValues) {
      const {
        validationErrors,
        address,
        english,
        attributes,
        // We omit willingAccepted on purpose
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        willingAccepted,
        ...profileData
      } = currValues as ProfileFormValues

      if (Object.values(validationErrors).filter(Boolean).length) {
        return
      }

      const languageAttribute = attributes.find((el) => el.type === 'languages')

      const englishOption = languages?.options.find((el) =>
        checkIsEnglish(el.text)
      )

      const newAttributes = [...attributes]
      if (languageAttribute && englishOption) {
        const languageAttributeIndex = attributes.findIndex(
          (el) => el.type === 'languages'
        )
        const selectedLanguagesValues =
          languageAttribute?.value as CatalogItem[]
        if (english?.value) {
          if (!selectedLanguagesValues?.find((el) => checkIsEnglish(el.text))) {
            newAttributes[languageAttributeIndex] = {
              ...languageAttribute,
              value: [...selectedLanguagesValues, englishOption],
            }
          }
        } else {
          newAttributes[languageAttributeIndex] = {
            ...languageAttribute,
            value: selectedLanguagesValues.filter(
              (el) => !checkIsEnglish(el.text)
            ),
          }
        }
      }

      updateUser.mutate({
        ...profileData,
        address: address?.address
          ? {
              ...address,
              id: user?.profile?.address?.id,
            }
          : undefined,
        attributes: newAttributes,
        profileImageUrl: user?.profile.profileImageUrl,
      } as unknown as UserProfile)
    }
    setActiveStep(id)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [isUserFetching, activeStep])

  useEffect(() => {
    setParams(
      (p) => {
        p.set('profile-form-step', String(activeStep))
        return p
      },
      { replace: true }
    )
  }, [activeStep])

  const steps = useMemo(() => {
    const contentTypes = user?.profile?.attributes?.find(
      (attr) => attr.type === 'content_types'
    )?.value as CatalogItem[]
    const beautyQuestions: CatalogItem[] = []
    contentTypes?.forEach((el) => {
      if (BEAUTY_QUESTIONS.includes(el.text)) {
        beautyQuestions.push(el)
      }
    })
    const currentSteps = [
      {
        id: 0,
        Component: () => <ProfileFormGetStarted />,
        onForward: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Welcome Video Page Completed`
          )
        },
        onBack: () => null,
      },
      {
        id: 1,
        Component: () => <ProfileFormReasonsToJoin />,
        validationSchema: reasonsToJoinValidationSchema,
        onForward: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Persona Page Completed`
          )
        },
        onBack: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - Persona Page`
          )
        },
      },
      {
        id: 2,
        Component: () => <ProfileFormGeneral />,
        validationSchema: () =>
          generalValidationSchema(addressField, isUKBasedUser),
        onForward: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: General Information Page Completed`
          )
        },
        onBack: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - General Information Page`
          )
        },
      },
      {
        id: 3,
        Component: () => <ProfileFormSocial />,
        validationSchema: socialValidationSchema,
        onForward: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Social Media Page Completed`
          )
        },
        onBack: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - Social Media Page`
          )
        },
      },
      {
        id: 4,
        Component: () => <ProfileFormAbout />,
        validationSchema: aboutValidationSchema,
        onForward: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: About Me Page Completed`
          )
        },
        onBack: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - About Me Page`
          )
        },
      },
      {
        id: 5,
        Component: () => <ReviewExperience />,
        validationSchema: contentValidationSchema,
        onForward: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Video Review Experience Page Completed`
          )
        },
        onBack: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - Video Review Experience Page`
          )
        },
      },
      {
        id: 7,
        Component: () => <ProfileFormApparelSizes />,
        validationSchema: sizesValidationSchema,
        onForward: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Fashion Page Completed`
          )
        },
        onBack: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - Fashion Page`
          )
        },
      },
      {
        id: 8,
        Component: () => <ProfileFormAgreement />,
        validationSchema: agreementValidationSchema,
        onForward: () => {
          return null
        },
        onBack: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - Creator Agreement Page`
          )
        },
      },
    ]

    if (beautyQuestions.length) {
      if (contentTypes?.length === beautyQuestions.length) {
        currentSteps[BEAUTY_QUESTION_INDEX] = {
          id: BEAUTY_QUESTION_INDEX,
          Component: () => <ProfileFormBeauty />,
          validationSchema: beautyValidationSchema,
          onForward: () => {
            analyticsTrack(
              `${ANALYTICS_EVENT_NAMES.application}: Beauty Page Completed`
            )
          },
          onBack: () => {
            analyticsTrack(
              `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - Beauty Page`
            )
          },
        }
      } else {
        currentSteps.splice(BEAUTY_QUESTION_INDEX, 0, {
          id: 6,
          Component: () => <ProfileFormBeauty />,
          validationSchema: beautyValidationSchema,
          onForward: () => {
            analyticsTrack(
              `${ANALYTICS_EVENT_NAMES.application}: Beauty Page Completed`
            )
          },
          onBack: () => {
            analyticsTrack(
              `${ANALYTICS_EVENT_NAMES.application}: Back Button Clicked - Beauty Page`
            )
          },
        })
      }
    }

    return currentSteps
  }, [user, addressField, isUKBasedUser])

  return (
    <ProfileFormContext.Provider
      value={{
        activeStep,
        stepCount: activeStep + 1,
        onStepChange,
        onCountryChange,
        steps,
        totalSteps: steps.length,
        user,
        isUserUpdating: updateUser.isLoading,
      }}
    >
      {children}
    </ProfileFormContext.Provider>
  )
}

export const useProfileForm = () => useContext(ProfileFormContext)

export default ProfileFormProvider
